import { storeToRefs } from 'pinia'
import { ref, watch } from 'vue'
import useDetailPanelStore from '@/stores/detailPanel'
import { jakartownsOptions, getAngleFromPoints } from '@/lib/jakartownsHelper.ts'

interface JakartownsMultipassItem {
  properties: {
    image_id: string
    date: string
  }
}

interface JakartownsPositionDetail {
  latitude: number
  longitude: number
  height?: number
  multipassAtLocation: JakartownsMultipassItem[]
  currentSphereInfo: {
    properties: {
      image_id: string
    }
  }
}

interface JakartownsViewer {
  setPosition: (pos: { latitude: number; longitude: number }) => void
  setPan: (angle: number) => void
  setFov: (fov: number) => void
  setTilt: (tilt: number) => void
  setImage: (imageId: string | null) => void
}

declare global {
  interface Window {
    jakartowns: {
      app: {
        create_jakartowns: (
          selector: string,
          options: typeof jakartownsOptions,
          cb: (viewer: JakartownsViewer) => void,
        ) => void
      }
    }
    jakartowns_instance?: JakartownsViewer
  }
  interface WindowEventMap {
    position: CustomEvent<JakartownsPositionDetail>
    rotation: CustomEvent<number>
    tilt: CustomEvent<number>
    fov: CustomEvent<number>
  }
}

export default function useJakartownsApi() {
  const customTilt = ref(0)
  const {
    selectedAsset,
    selectedAssetPosition,
    observerPosition,
    rotation,
    tilt,
    fov,
    availableImages,
    currentImageId,
    isPanelDisplayed,
  } = storeToRefs(useDetailPanelStore())

  function updateJakartowns(options: any) {
    if (selectedAssetPosition.value) {
      const { lat: latitude, lng: longitude } = selectedAssetPosition.value
      if (window.jakartowns_instance === undefined) {
        return
      }
      window.jakartowns_instance.setPosition({ latitude, longitude })
    }
  }

  function createJakartowns(options: any) {
    const hasInstanceRef = ref(false)

    watch(hasInstanceRef, (newVal) => {
      if (!newVal) return
      updateJakartowns(options)
    })

    window.jakartowns.app.create_jakartowns(
      '#detail-panel__jakartowns__app',
      jakartownsOptions,
      (viewer: JakartownsViewer) => {
        window.jakartowns_instance = viewer
        hasInstanceRef.value = true

        window.addEventListener('position', (e: CustomEvent<JakartownsPositionDetail>) => {
          observerPosition.value = {
            lat: e.detail.latitude,
            lng: e.detail.longitude,
            height: e.detail.height || 0
          }

          if (selectedAssetPosition.value) {
            const angle = getAngleFromPoints(
              observerPosition.value,
              selectedAssetPosition.value,
            )
            window.jakartowns_instance?.setPan(angle)
            rotation.value = angle
          }

          window.jakartowns_instance?.setFov(80)
          window.jakartowns_instance?.setTilt(customTilt.value)

          availableImages.value = e.detail.multipassAtLocation.map(
            ({ properties: { image_id: imageUid, date } }) => ({
              imageUid,
              date,
            }),
          )
          currentImageId.value = e.detail.currentSphereInfo.properties.image_id

        })

        window.addEventListener('rotation', (e: CustomEvent<number>) => {
          rotation.value = e.detail
        })

        window.addEventListener('tilt', (e: CustomEvent<number>) => {
          tilt.value = e.detail
        })

        window.addEventListener('fov', (e: CustomEvent<number>) => {
          fov.value = e.detail
        })

        watch(currentImageId, (newImageId) => {
          if (!isPanelDisplayed.value) return
          window.jakartowns_instance?.setImage(newImageId)
        })
      },
    )
  }

  return (options: any = null, tilt = 0) => {
    customTilt.value = tilt
    if (window.jakartowns_instance === undefined) {
      createJakartowns(options)
      return
    }
    updateJakartowns(options)
  }
}
